import type { StorefrontThemeSettings } from "../../app/models/StorefrontThemeSettings";
import type {
    StorefrontCollectionOption,
    StorefrontCuratedGroup,
    StorefrontResultsSettings,
    StorefrontUiSettings,
} from "../../app/models/StorefrontResultsSettings";
import { DEFAULT_UI_SETTINGS } from "./storefrontUiDefaults";

// Shared defaults, snapshots and normalizers for the storefront theme and results settings.

export const DEFAULT_THEME_SETTINGS: StorefrontThemeSettings = {
    isThemeSet: true,
    promptMake: "---Select Make---",
    promptModel: "---Select Model---",
    promptYear: "---Select Year---",
    promptSubmodel: "---Select Submodel---",
    promptSearch: "Search",
    promptClear: "Clear",
    redirectPath: "/collections/all/{vehicle}",
    collectionTagFilterPattern: "?filter.p.tag={vehicle}",
    automaticSearch: false,
    ymmEnabled: false,
    sortYearsASC: false,
    redirectToResults: false,
    customRedirectEnabled: false,
    globalFilter: false,
    collectionRedirectEnabled: false,
    collectionTagFilterEnabled: false,
    clearButtonRedirectEnabled: false,
    includeUniversalCollections: false,
    useTwoTags: false,
    useThreeTags: false,
    isPartialSearch: false,
    isCollectionMetaField: false,
    metaDataSyncEnabled: false,
    submodelEnabled: false,
    tagPrefixEnabled: true,
    saveTagToCustomerProfile: false,
    onlyShowMappedUserVehicles: true,
    searchableDropdownsEnabled: false,
    autoOpenNextDropdown: true,
    renderingMode: "CollectionFiltering",
    resultsReturnType: "Products",
    resultsGroupingMode: "None",
    priceDisplayMode: "MarketAwarePrices",
    variantResultDisplayMode: "TopLevelProducts",
};

export const DEFAULT_RESULTS_SETTINGS: StorefrontResultsSettings = {
    renderingMode: "CollectionFiltering",
    returnResultsAs: "Products",
    groupResultsBy: "None",
    priceDisplayMode: "MarketAwarePrices",
    variantResultDisplayMode: "TopLevelProducts",
    productTypeFilterMode: "All",
    productTypeFilterValues: [],
    collectionFilterMode: "All",
    collectionFilterIds: [],
    uiSettings: DEFAULT_UI_SETTINGS,
    curatedGroups: [],
    availableProductTypes: [],
    availableCollections: [],
};

export type SearchDestinationMode = "CurrentCollection" | "ResultsPage";
export type ResultsPageLocationMode = "Default" | "Custom";
export type ShopifyFilteringMethod = "StorefrontFiltering" | "LegacyTagUrl";

type UiColorOverrideKey =
    | "searchBarBackground"
    | "searchButtonBackground"
    | "searchButtonText"
    | "clearButtonBackground"
    | "clearButtonText"
    | "selectedVehicleBackground"
    | "selectedVehicleText"
    | "changeButtonBackground"
    | "changeButtonText"
    | "headingBackground"
    | "headingText"
    | "cardBackground"
    | "cardText"
    | "buttonBackground"
    | "buttonText"
    | "exactBadgeColor"
    | "universalBadgeColor"
    | "hoverBorderColor"
    | "reviewStarColor"
    | "reviewEmptyStarColor";
const uiColorOverrideKeys: UiColorOverrideKey[] = [
    "searchBarBackground",
    "searchButtonBackground",
    "searchButtonText",
    "clearButtonBackground",
    "clearButtonText",
    "selectedVehicleBackground",
    "selectedVehicleText",
    "changeButtonBackground",
    "changeButtonText",
    "headingBackground",
    "headingText",
    "cardBackground",
    "cardText",
    "buttonBackground",
    "buttonText",
    "exactBadgeColor",
    "universalBadgeColor",
    "hoverBorderColor",
    "reviewStarColor",
    "reviewEmptyStarColor",
];

export const getSearchDestinationMode = (theme?: StorefrontThemeSettings): SearchDestinationMode =>
    theme?.redirectToResults ? "ResultsPage" : "CurrentCollection";

export const getResultsPageLocationMode = (theme?: StorefrontThemeSettings): ResultsPageLocationMode =>
    theme?.customRedirectEnabled ? "Custom" : "Default";

export const getShopifyFilteringMethod = (theme?: StorefrontThemeSettings): ShopifyFilteringMethod =>
    theme?.collectionTagFilterEnabled ? "StorefrontFiltering" : "LegacyTagUrl";

export const stripVehiclePlaceholderFromPath = (path?: string): string => {
    const normalized = (path || "/collections/all").trim() || "/collections/all";
    return normalized
        .replace(/\/+\{vehicle\}/gi, "")
        .replace(/\{vehicle\}/gi, "")
        .replace(/\/+$/g, "") || "/collections/all";
};

export const resultSettingsSnapshot = (settings?: StorefrontResultsSettings) => JSON.stringify({
    renderingMode: settings?.renderingMode ?? DEFAULT_RESULTS_SETTINGS.renderingMode,
    returnResultsAs: settings?.returnResultsAs ?? DEFAULT_RESULTS_SETTINGS.returnResultsAs,
    groupResultsBy: settings?.groupResultsBy ?? DEFAULT_RESULTS_SETTINGS.groupResultsBy,
    priceDisplayMode: settings?.priceDisplayMode ?? DEFAULT_RESULTS_SETTINGS.priceDisplayMode,
    variantResultDisplayMode: settings?.variantResultDisplayMode ?? DEFAULT_RESULTS_SETTINGS.variantResultDisplayMode,
    productTypeFilterMode: settings?.productTypeFilterMode ?? DEFAULT_RESULTS_SETTINGS.productTypeFilterMode,
    productTypeFilterValues: [...(settings?.productTypeFilterValues ?? [])].sort((left, right) => left.localeCompare(right)),
    collectionFilterMode: settings?.collectionFilterMode ?? DEFAULT_RESULTS_SETTINGS.collectionFilterMode,
    collectionFilterIds: [...(settings?.collectionFilterIds ?? [])].sort((left, right) => left - right),
    uiSettings: uiSettingsSnapshot(settings?.uiSettings),
    curatedGroups: [...(settings?.curatedGroups ?? [])]
        .map((group) => ({
            id: group.id ?? null,
            name: group.name.trim(),
            include: group.include,
            displayOrder: Number(group.displayOrder) || 0,
            collectionIds: [...(group.collectionIds ?? [])].sort((left, right) => left - right),
        }))
        .sort((left, right) => left.displayOrder - right.displayOrder || left.name.localeCompare(right.name)),
});

export const themeSettingsSnapshot = (
    theme: StorefrontThemeSettings | undefined,
    persistentFilteringChecked: boolean,
    submodelEnabled: boolean
) => JSON.stringify({
    redirectPath: theme?.redirectPath ?? DEFAULT_THEME_SETTINGS.redirectPath,
    collectionTagFilterPattern: theme?.collectionTagFilterPattern ?? DEFAULT_THEME_SETTINGS.collectionTagFilterPattern,
    automaticSearch: !!theme?.automaticSearch,
    ymmEnabled: !!theme?.ymmEnabled,
    sortYearsASC: !!theme?.sortYearsASC,
    redirectToResults: !!theme?.redirectToResults,
    customRedirectEnabled: !!theme?.customRedirectEnabled,
    collectionRedirectEnabled: !!theme?.collectionRedirectEnabled,
    collectionTagFilterEnabled: !!theme?.collectionTagFilterEnabled,
    clearButtonRedirectEnabled: !!theme?.clearButtonRedirectEnabled,
    includeUniversalCollections: !!theme?.includeUniversalCollections,
    useTwoTags: !!theme?.useTwoTags,
    useThreeTags: !!theme?.useThreeTags,
    isPartialSearch: !!theme?.isPartialSearch,
    isCollectionMetaField: !!theme?.isCollectionMetaField,
    metaDataSyncEnabled: !!theme?.metaDataSyncEnabled,
    saveTagToCustomerProfile: !!theme?.saveTagToCustomerProfile,
    onlyShowMappedUserVehicles: theme?.onlyShowMappedUserVehicles ?? true,
    searchableDropdownsEnabled: !!theme?.searchableDropdownsEnabled,
    autoOpenNextDropdown: theme?.autoOpenNextDropdown ?? true,
    persistentFilteringChecked,
    submodelEnabled,
});

export const cloneResultsSettings = (settings: StorefrontResultsSettings): StorefrontResultsSettings => ({
    ...settings,
    productTypeFilterValues: [...settings.productTypeFilterValues],
    collectionFilterIds: [...settings.collectionFilterIds],
    uiSettings: withUiDefaults(settings.uiSettings),
    curatedGroups: settings.curatedGroups.map((group) => ({
        ...group,
        collectionIds: [...group.collectionIds],
    })),
    availableProductTypes: [...settings.availableProductTypes],
    availableCollections: [...settings.availableCollections],
});

export const createBlankGroup = (displayOrder: number): StorefrontCuratedGroup => ({
    name: "",
    include: true,
    displayOrder,
    collectionIds: [],
});

export const collectionLabel = (collection: StorefrontCollectionOption) =>
    collection.handle ? `${collection.title} (${collection.handle})` : collection.title;

const normalizeHexColor = (value: string | undefined, fallback: string) =>
    /^#[0-9a-fA-F]{6}$/.test(value || "") ? value! : fallback;

export const getInheritedUiColors = (settings: StorefrontUiSettings): Record<UiColorOverrideKey, string> => ({
    searchBarBackground: settings.globalSubtleBackgroundColor,
    searchButtonBackground: settings.globalPrimaryColor,
    searchButtonText: settings.globalInverseTextColor,
    clearButtonBackground: settings.globalSurfaceColor,
    clearButtonText: settings.globalTextColor,
    selectedVehicleBackground: settings.globalPrimaryColor,
    selectedVehicleText: settings.globalInverseTextColor,
    changeButtonBackground: settings.globalSurfaceColor,
    changeButtonText: settings.globalTextColor,
    headingBackground: settings.globalTextColor,
    headingText: settings.globalInverseTextColor,
    cardBackground: settings.globalSurfaceColor,
    cardText: settings.globalTextColor,
    buttonBackground: settings.globalPrimaryColor,
    buttonText: settings.globalInverseTextColor,
    exactBadgeColor: settings.globalAccentColor,
    universalBadgeColor: settings.globalMutedTextColor,
    hoverBorderColor: settings.globalPrimaryColor,
    reviewStarColor: settings.globalReviewStarColor,
    reviewEmptyStarColor: settings.globalBorderColor,
});

const normalizeUiColorOverrides = (overrides?: string[]) =>
    uiColorOverrideKeys.filter((key) => overrides?.includes(key));

const inferLegacyUiColorOverrides = (settings: StorefrontUiSettings) => {
    const inherited = getInheritedUiColors(settings);
    return uiColorOverrideKeys.filter((key) => normalizeHexColor(settings[key], inherited[key]).toLowerCase() !== inherited[key].toLowerCase());
};

export const withUiDefaults = (settings?: Partial<StorefrontUiSettings>): StorefrontUiSettings => {
    const hasExplicitOverrides = Array.isArray(settings?.colorOverrides);
    const merged = {
        ...DEFAULT_UI_SETTINGS,
        ...(settings ?? {}),
        colorOverrides: hasExplicitOverrides ? [...(settings?.colorOverrides ?? [])] : [],
    } as StorefrontUiSettings;

    const normalizedGlobals = {
        ...merged,
        globalPrimaryColor: normalizeHexColor(merged.globalPrimaryColor, DEFAULT_UI_SETTINGS.globalPrimaryColor),
        globalSecondaryColor: normalizeHexColor(merged.globalSecondaryColor, DEFAULT_UI_SETTINGS.globalSecondaryColor),
        globalAccentColor: normalizeHexColor(merged.globalAccentColor, DEFAULT_UI_SETTINGS.globalAccentColor),
        globalSurfaceColor: normalizeHexColor(merged.globalSurfaceColor, DEFAULT_UI_SETTINGS.globalSurfaceColor),
        globalSubtleBackgroundColor: normalizeHexColor(merged.globalSubtleBackgroundColor, DEFAULT_UI_SETTINGS.globalSubtleBackgroundColor),
        globalTextColor: normalizeHexColor(merged.globalTextColor, DEFAULT_UI_SETTINGS.globalTextColor),
        globalInverseTextColor: normalizeHexColor(merged.globalInverseTextColor, DEFAULT_UI_SETTINGS.globalInverseTextColor),
        globalMutedTextColor: normalizeHexColor(merged.globalMutedTextColor, DEFAULT_UI_SETTINGS.globalMutedTextColor),
        globalBorderColor: normalizeHexColor(merged.globalBorderColor, DEFAULT_UI_SETTINGS.globalBorderColor),
        globalReviewStarColor: normalizeHexColor(merged.globalReviewStarColor, DEFAULT_UI_SETTINGS.globalReviewStarColor),
    };

    return {
        ...normalizedGlobals,
        colorOverrides: hasExplicitOverrides
            ? normalizeUiColorOverrides(normalizedGlobals.colorOverrides)
            : inferLegacyUiColorOverrides(normalizedGlobals),
    };
};

// Effective colors are inherited from Global style until a specific control is edited.
export const getEffectiveUiSettings = (settings: StorefrontUiSettings): StorefrontUiSettings => {
    const base = withUiDefaults(settings);
    const inherited = getInheritedUiColors(base);
    const overrides = new Set(base.colorOverrides);
    const effective = { ...base };

    uiColorOverrideKeys.forEach((key) => {
        effective[key] = overrides.has(key)
            ? normalizeHexColor(base[key], inherited[key])
            : inherited[key];
    });

    return effective;
};

const uiSettingsSnapshot = (settings?: StorefrontUiSettings) =>
    JSON.stringify(withUiDefaults(settings));

export const withResultsDefaults = (settings?: Partial<StorefrontResultsSettings>): StorefrontResultsSettings => ({
    ...DEFAULT_RESULTS_SETTINGS,
    ...(settings ?? {}),
    productTypeFilterValues: [...(settings?.productTypeFilterValues ?? DEFAULT_RESULTS_SETTINGS.productTypeFilterValues)],
    collectionFilterIds: [...(settings?.collectionFilterIds ?? DEFAULT_RESULTS_SETTINGS.collectionFilterIds)],
    uiSettings: withUiDefaults(settings?.uiSettings),
    curatedGroups: [...(settings?.curatedGroups ?? DEFAULT_RESULTS_SETTINGS.curatedGroups)],
    availableProductTypes: [...(settings?.availableProductTypes ?? DEFAULT_RESULTS_SETTINGS.availableProductTypes)],
    availableCollections: [...(settings?.availableCollections ?? DEFAULT_RESULTS_SETTINGS.availableCollections)],
});
